import { type MutationHookOptions, useMutation } from '@apollo/client'

import { notEmpty } from 'Modules/order/utils/notEmpty.utils'

import { handleFetchErrorFunctional } from 'Utilities'

import { SentryErrorType } from 'Constants/errors'
import type {
  mutationCreateBulkOfferOrders,
  mutationCreateBulkOfferOrdersVariables,
} from 'Constants/graphql/mutations/__generated__/mutationCreateBulkOfferOrders'
import type {
  mutationCreateOfferOrders,
  mutationCreateOfferOrdersVariables,
} from 'Constants/graphql/mutations/__generated__/mutationCreateOfferOrders'
import { MUTATION_CREATE_BULK_OFFER_ORDERS } from 'Constants/graphql/mutations/MutationCreateBulkOfferOrders'
import { MUTATION_CREATE_OFFER_ORDERS } from 'Constants/graphql/mutations/MutationCreateOfferOrders'

import { useStore } from 'Portal/src/stores'

import type { UseOfferOrderMutationArgs } from './useOfferOrderMutation.types'
import { isBulkOfferOrder } from './useOfferOrderMutation.utils'

/**
 * This hook is used to generate appropriate mutation and its handlers
 * for placing an order for catalog and product offers.
 */
export const useOfferOrderMutation = (args: UseOfferOrderMutationArgs) => {
  const { handleOnCompletedOrder, variables, handleOnErrorOrder } = args
  const { orderStore } = useStore()
  const { setOrderedDevices, setMutationError } = orderStore
  const isBulkOrder = isBulkOfferOrder(variables)

  const options: MutationHookOptions<
    mutationCreateOfferOrders,
    mutationCreateOfferOrdersVariables
  > = {
    onCompleted: data => {
      const offerOrders = data.createOfferOrders.offerOrders?.filter(notEmpty)

      if (offerOrders?.[0]?.caseItems) {
        const filteredDevices = offerOrders[0].caseItems.filter(notEmpty)

        setOrderedDevices(filteredDevices)
      }

      setMutationError(false)

      if (handleOnCompletedOrder) {
        handleOnCompletedOrder(data)
      }
    },
    onError: (error, options) => {
      handleFetchErrorFunctional()(error, {
        location: window?.location || undefined,
        operation: {
          operationName: isBulkOrder
            ? 'createBulkOfferOrders'
            : 'createOfferOrders',
          variables: options?.variables ?? undefined,
        },
        type: SentryErrorType.GQL_ERROR,
      })
      setMutationError(true)

      if (handleOnErrorOrder) {
        handleOnErrorOrder(error)
      }
    },
    variables,
  }

  const [createOfferOrders, { loading: isCreateOfferOrdersLoading }] =
    useMutation<mutationCreateOfferOrders, mutationCreateOfferOrdersVariables>(
      MUTATION_CREATE_OFFER_ORDERS,
      options,
    )
  const [createBulkOfferOrders, { loading: isCreateBulkOfferOrdersLoading }] =
    useMutation<
      mutationCreateBulkOfferOrders,
      mutationCreateBulkOfferOrdersVariables
    >(MUTATION_CREATE_BULK_OFFER_ORDERS, options)

  return {
    handleOfferOrderMutation: isBulkOrder
      ? createBulkOfferOrders
      : createOfferOrders,
    isOfferOrderLoading:
      isCreateOfferOrdersLoading || isCreateBulkOfferOrdersLoading,
  }
}
